import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

// Components
import { ButtonComponent } from '@shared-components/button/button.component';
import { ActionButtonModule } from '@shared-components/action-button';
import { CardComponent } from '@shared-components/card/card.component';
import { CollapsableComponent } from '@shared-components/collapsable/collapsable.component';
import { FooterComponent } from '@footer/footer.component';
import { HeaderComponent } from '@header/header.component';
import { NotificationBellComponent } from './layout/components/notification-bell/notification-bell.component';
import { InfoItemComponent } from './components/info-item-component/info-item-component';
import { HelpComponent } from '@shared-components/help-component/help.component';
import { LayoutComponent } from './layout/layout.component';
import { NotFoundComponent } from '@shared-components/not-found-component/not-found.component';
import { EmptyComponent } from './components/empty/empty.component';
import { SidebarComponent } from '@sidebar/sidebar.component';

// Material
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';

// Pipes
import { CuitPipe } from './pipes/cuit.pipe';
import { DniCuitPipe } from '@shared-pipes/dni-cuit.pipe';

import { EntidadNombrePipe, EntidadNombreJuzgadoPipe } from './pipes/entidad.pipe';
import { MunicipioDescripcionPipe } from './pipes/municipio-descripcion.pipe';
import { ParametroDescripcionPipe } from './pipes/parametro-descripcion.pipe';
import { PasswordPipe } from './pipes/password.pipe';
import { RazonSocialPipe } from './pipes/razon-social.pipe';
import { UsuarioNombrePipe } from './pipes/usuario-nombre.pipe';
import { TelefonoFormatPipe } from './pipes/telefono.pipe';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { EncabezadoComponent } from './components/encabezado-component/encabezado.component';
import { MenubarModule } from 'primeng/menubar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { RippleModule } from 'primeng/ripple';
import { SkeletonModule } from 'primeng/skeleton';
import { TitleComponent } from '@shared-components/title/title.component';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { AccordionModule } from 'primeng/accordion';
import { BlockUIModule } from 'primeng/blockui';
import { CalendarModule } from 'primeng/calendar';
import { CarouselModule } from 'primeng/carousel';

import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { EditorModule } from 'primeng/editor';
import { FieldsetModule } from 'primeng/fieldset';
import { FileUploadModule } from 'primeng/fileupload';
import { GalleriaModule } from 'primeng/galleria';
import { ImageModule } from 'primeng/image';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputTextModule } from 'primeng/inputtext';
import { KeyFilterModule } from 'primeng/keyfilter';

import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';

import { SelectButtonModule } from 'primeng/selectbutton';

import { StepsModule } from 'primeng/steps';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';

import { ToolbarModule } from 'primeng/toolbar';
import { ConfirmationService } from 'primeng/api';

// Lucide Angular
import {
  Bell,
  Building,
  ChevronDown,
  ChevronUp,
  ClipboardPlus,
  Minus,
  FileSearch,
  Landmark,
  BarChart3,
  LogOut,
  LucideAngularModule,
  MonitorSmartphone,
  Ruler,
  Scale,
  UserCog,
  UserRoundCheck,
  Warehouse,
} from 'lucide-angular';
import { Menu, Home, Settings, User, ChevronRight } from 'lucide-angular';
import { RetencionesIcon } from './icons/custom-icons';
import { SublevelMenuComponent } from '@layout/components/sidebar/sublevel-menu.component';
import { MotivoAnulacionDialogComponent } from '@shared-components/motivo-anulacion-dialog/motivo-anulacion-dialog.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SummaryCardComponent } from '@shared-components/summary-card/summary-card.component';
import { TableLegendComponent } from '@shared-components/table-legend/table-legend.component';
import { TextSkeletonComponent } from '@shared-components/text-skeleton/text-skeleton.component';
import { FileUploadComponent } from '@shared-components/file-upload/file-upload.component';
import { PublicLayoutComponent } from './layout/components/public/public-layout/public-layout.component';

@NgModule({
  declarations: [
    ButtonComponent,
    CardComponent,
    CollapsableComponent,
    CuitPipe,
    DniCuitPipe,
    EncabezadoComponent,
    EntidadNombreJuzgadoPipe,
    EntidadNombrePipe,
    FooterComponent,
    HeaderComponent,
    HelpComponent,
    LayoutComponent,
    MunicipioDescripcionPipe,
    NotFoundComponent,
    ParametroDescripcionPipe,
    PasswordPipe,
    RazonSocialPipe,
    UsuarioNombrePipe,
    SidebarComponent,
    TelefonoFormatPipe,
    TextSkeletonComponent,
    TitleComponent,
    EmptyComponent,
    SublevelMenuComponent,
    MotivoAnulacionDialogComponent,
    FileUploadComponent,
    PublicLayoutComponent,
    SummaryCardComponent,
    TableLegendComponent,
  ],
  imports: [
    // Módulos base de Angular
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    // Módulos de componentes propios
    ActionButtonModule,

    // Módulos de PrimeNG (ordenados alfabéticamente)
    AccordionModule,
    BlockUIModule,
    ButtonModule,
    CalendarModule,
    CardModule,
    CarouselModule,
    CheckboxModule,
    ConfirmDialogModule,
    DialogModule,
    DividerModule,
    DropdownModule,
    DynamicDialogModule,
    EditorModule,
    FieldsetModule,
    FileUploadModule,
    GalleriaModule,
    ImageModule,
    InfoItemComponent,
    NotificationBellComponent,
    InputNumberModule,
    InputMaskModule,
    InputTextareaModule,
    InputTextModule,
    KeyFilterModule,
    MenubarModule,
    MessageModule,
    MultiSelectModule,
    PanelModule,
    PanelMenuModule,
    ProgressBarModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    RippleModule,
    SelectButtonModule,
    SkeletonModule,
    StepsModule,
    TableModule,
    TabViewModule,
    TagModule,
    ToastModule,
    ToolbarModule,
    TooltipModule,
    FileUploadModule,

    // Módulos de Angular Material
    MatSidenavModule,
    MatToolbarModule,

    // Módulos de Iconos (Lucide)
    LucideAngularModule.pick({
      BarChart3,
      Bell,
      Building,
      ChevronDown,
      ChevronRight,
      ChevronUp,
      ClipboardPlus,
      Minus,
      FileSearch,
      Home,
      Landmark,
      LogOut,
      Menu,
      MonitorSmartphone,
      Ruler,
      Scale,
      Settings,
      User,
      UserCog,
      UserRoundCheck,
      Warehouse,
      RetencionesIcon,
    }),
  ],
  exports: [
    ActionButtonModule,
    CardComponent,
    ButtonComponent,
    CollapsableComponent,
    EncabezadoComponent,
    FooterComponent,
    HeaderComponent,
    HelpComponent,
    LayoutComponent,
    NotFoundComponent,
    SidebarComponent,
    TextSkeletonComponent,
    TitleComponent,
    InfoItemComponent,
    EmptyComponent,
    MotivoAnulacionDialogComponent,
    FileUploadComponent,
    SummaryCardComponent,
    TableLegendComponent,

    // Pipes para ser usados fuera
    CuitPipe,
    DniCuitPipe,
    EntidadNombrePipe,
    EntidadNombreJuzgadoPipe,
    MunicipioDescripcionPipe,
    ParametroDescripcionPipe,
    PasswordPipe,
    RazonSocialPipe,
    UsuarioNombrePipe,
    TelefonoFormatPipe,

    // Módulos de PrimeNG que podrías necesitar en otros componentes
    ButtonModule,
    CardModule,
    ConfirmDialogModule,
    DialogModule,
    DividerModule,
    DropdownModule,
    InputTextModule,
    MessageModule,
    RippleModule,
    TableModule,
    ToastModule,
    TooltipModule,
    SkeletonModule,
    LucideAngularModule
  ],
  providers: [ConfirmationService],
})
export class SharedModule {}
